import React, { useState } from 'react';
import { useAPR } from '../context/APRContext';
import { Customer } from '../types';

const Customers: React.FC = () => {
  const { state, dispatch } = useAPR();
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingCustomer) {
      const updatedCustomer: Customer = {
        ...editingCustomer,
        name: formData.name,
        description: formData.description
      };
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } else {
      const newCustomer: Customer = {
        id: `customer_${Date.now()}`,
        name: formData.name,
        description: formData.description,
        applications: []
      };
      dispatch({ type: 'ADD_CUSTOMER', payload: newCustomer });
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setShowForm(false);
    setEditingCustomer(null);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      description: customer.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = (customerId: string) => {
    if (window.confirm('Are you sure you want to delete this customer and all associated applications?')) {
      dispatch({ type: 'DELETE_CUSTOMER', payload: customerId });
    }
  };

  const customerApplications = (customerId: string) => {
    return state.applications.filter(app => app.customerId === customerId);
  };

  return (
    <div className="customers-page">
      <div className="page-header">
        <h1>Customer Management</h1>
        <p>Manage customers and their application portfolios</p>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add New Customer
        </button>
      </div>

      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content">
            <div className="form-modal-header">
              <h2>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
              <button 
                className="btn-close"
                onClick={resetForm}
                aria-label="Close form"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="customer-form">
              <div className="form-group">
                <label htmlFor="name">Customer Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter customer name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Enter customer description"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCustomer ? 'Update Customer' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="customers-grid">
        {state.customers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h3>No Customers Yet</h3>
            <p>Get started by adding your first customer to begin application portfolio assessment.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              Add First Customer
            </button>
          </div>
        ) : (
          state.customers.map(customer => (
            <div key={customer.id} className="customer-card">
              <div className="customer-card-header">
                <div className="customer-info">
                  <h3>{customer.name}</h3>
                  {customer.description && <p>{customer.description}</p>}
                </div>
                <div className="customer-actions">
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEdit(customer)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(customer.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="customer-stats">
                <div className="stat-item">
                  <span className="stat-value">{customerApplications(customer.id).length}</span>
                  <span className="stat-label">Applications</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {customerApplications(customer.id).filter(app => app.recommendation).length}
                  </span>
                  <span className="stat-label">Assessed</span>
                </div>
              </div>
              
              <div className="customer-card-footer">
                <button className="btn btn-outline-primary">
                  View Applications
                </button>
                <button className="btn btn-outline-secondary">
                  Start Assessment
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Customers;